import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { X, ChartLine, CircleNotch, TrendUp, TrendDown } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { ChartWatermark } from "@/components/FarmwatchLogo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function StockPriceHistoryModal({ 
  isOpen, 
  onClose, 
  ticker,
  stockName,
  currentPrice,
  changePercent,
  currency
}) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(90);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/agri-stocks/history/${ticker}`, {
        params: { days: period }
      });
      
      // Update current price from props if available
      const historyData = response.data;
      if (currentPrice && historyData.history && historyData.history.length > 0) {
        historyData.history[historyData.history.length - 1].price = currentPrice;
        historyData.stats.current = currentPrice;
      }
      
      setData(historyData);
    } catch (err) {
      console.error("Error fetching stock history:", err);
      setError("Failed to load price history");
    } finally {
      setLoading(false);
    }
  }, [ticker, period, currentPrice]);

  useEffect(() => {
    if (isOpen && ticker) {
      fetchHistory();
    }
  }, [isOpen, ticker, fetchHistory]);

  if (!isOpen) return null;

  const periodOptions = [
    { value: 30, label: "30D" },
    { value: 90, label: "90D" },
    { value: 180, label: "6M" },
    { value: 365, label: "1Y" },
  ];

  const formatCurrency = (value) => {
    if (!value && value !== 0) return "N/A";
    if (currency === "GBP") return `£${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (currency === "EUR") return `€${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const currencySymbol = currency === "GBP" ? "£" : (currency === "EUR" ? "€" : "$");

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
      onClick={onClose}
    >
      <div 
        className="bg-[#111827] border border-[#1F2937] w-full max-w-4xl max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#1F2937]">
          <div className="flex items-center gap-3">
            <ChartLine size={24} className="text-[#0F766E]" />
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-lg font-bold text-white">{ticker}</span>
                {changePercent !== undefined && (
                  <span className={`flex items-center gap-1 text-sm font-mono ${changePercent >= 0 ? 'text-[#059669]' : 'text-[#DC2626]'}`}>
                    {changePercent >= 0 ? <TrendUp size={14} /> : <TrendDown size={14} />}
                    {changePercent >= 0 ? '+' : ''}{changePercent.toFixed(2)}%
                  </span>
                )}
              </div>
              <p className="text-sm text-[#8B8580]">{stockName}</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-[#1F2937] transition-colors"
            data-testid="close-modal"
          >
            <X size={20} className="text-[#8B8580]" />
          </button>
        </div>

        {/* Period Selector */}
        <div className="flex items-center gap-2 p-4 border-b border-[#1F2937]">
          <span className="text-xs text-[#6B6661] mr-2">Period:</span>
          {periodOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={`px-3 py-1 text-xs font-mono transition-colors ${
                period === opt.value
                  ? "bg-[#0F766E] text-white"
                  : "bg-[#1F2937] text-[#8B8580] hover:text-white"
              }`}
              data-testid={`period-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <CircleNotch size={40} className="animate-spin text-[#0F766E]" />
            </div>
          ) : error ? (
            <div className="text-center py-20 text-[#DC2626]">{error}</div>
          ) : data ? (
            <>
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-[#1F2937] p-3">
                  <span className="text-xs text-[#6B6661] block">Current</span>
                  <span className="font-mono text-lg text-white">{formatCurrency(currentPrice || data.stats.current)}</span>
                </div>
                <div className="bg-[#1F2937] p-3">
                  <span className="text-xs text-[#6B6661] block">Average</span>
                  <span className="font-mono text-lg text-[#8B8580]">{formatCurrency(data.stats.average)}</span>
                </div>
                <div className="bg-[#1F2937] p-3">
                  <span className="text-xs text-[#6B6661] block">High</span>
                  <span className="font-mono text-lg text-[#059669]">{formatCurrency(data.stats.max)}</span>
                </div>
                <div className="bg-[#1F2937] p-3">
                  <span className="text-xs text-[#6B6661] block">Low</span>
                  <span className="font-mono text-lg text-[#DC2626]">{formatCurrency(data.stats.min)}</span>
                </div>
                <div className="bg-[#1F2937] p-3">
                  <span className="text-xs text-[#6B6661] block">30D Change</span>
                  <span className={`font-mono text-lg ${data.stats.change_30d >= 0 ? "text-[#059669]" : "text-[#DC2626]"}`}>
                    {data.stats.change_30d >= 0 ? "+" : ""}{data.stats.change_30d_percent}%
                  </span>
                </div>
              </div>

              {/* Chart */}
              <div className="h-[300px] relative">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.history}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#6B7280"
                      tick={{ fontSize: 10 }}
                      tickFormatter={(val) => {
                        const d = new Date(val);
                        return `${d.getDate()}/${d.getMonth() + 1}`;
                      }}
                    />
                    <YAxis 
                      stroke="#6B7280"
                      tick={{ fontSize: 10 }}
                      domain={['auto', 'auto']}
                      tickFormatter={(val) => `${currencySymbol}${val}`}
                    />
                    <Tooltip 
                      content={({ active, payload, label }) => {
                        if (!active || !payload || payload.length === 0) return null;
                        return (
                          <div className="bg-[#1F2937] border border-[#374151] p-3">
                            <p className="text-[#8B8580] mb-2 text-sm">
                              {new Date(label).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                            {payload.map((entry, index) => {
                              const color = entry.name === "Price" ? "#FFFFFF" : entry.color;
                              let displayName = entry.name;
                              if (entry.name === "ma_7") displayName = "7D MA";
                              else if (entry.name === "ma_30") displayName = "30D MA";
                              return (
                                <p key={index} style={{ color }} className="text-sm my-1">
                                  {displayName}: {formatCurrency(entry.value)}
                                </p>
                              );
                            })}
                          </div>
                        );
                      }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="price" 
                      stroke="#0F766E" 
                      strokeWidth={2}
                      dot={false}
                      name="Price"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="ma_7" 
                      stroke="#10B981" 
                      strokeWidth={1}
                      strokeDasharray="3 3"
                      dot={false}
                      name="7D MA"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="ma_30" 
                      stroke="#064E3B" 
                      strokeWidth={1}
                      strokeDasharray="5 5"
                      dot={false}
                      name="30D MA"
                    />
                  </LineChart>
                </ResponsiveContainer>
                <ChartWatermark position="bottom-right" size="small" />
              </div>

              {/* Exchange Info */}
              <div className="mt-4 text-center">
                <span className="text-xs text-[#6B6661]">
                  Data from Yahoo Finance • {data.history.length} trading days • {currency}
                </span>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
